import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Trash2, Check, X, Users, Loader2, ArrowLeft, QrCode } from 'lucide-react';
import {
  ListShareCard,
  StatsPanel,
  GuestListToolbar,
  PromoteButton,
} from '../components/promoter/GuestListExtras';
import CreateListDialog from '../components/promoter/CreateListDialog';
import AddGuestDialog from '../components/promoter/AddGuestDialog';
import GuestQrDialog from '../components/promoter/GuestQrDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Promoter-facing dashboard: create guest lists per event, add guests (with optional
 * KYC photo), and check guests in at the door. Admins can use this too.
 */
const PromoterDashboard = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [guestLists, setGuestLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState(null);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create list dialog
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [listForm, setListForm] = useState({ event_id: '', name: '', max_capacity: '', wait_list_mode: false });

  // Event-code lookup for the create-list flow
  const [codeInput, setCodeInput] = useState('');
  const [codeLookup, setCodeLookup] = useState(null); // { id, title, date, time, venue, image_url, event_code, my_list_count }
  const [codeLookupLoading, setCodeLookupLoading] = useState(false);

  // Add guest dialog
  const [guestDialogOpen, setGuestDialogOpen] = useState(false);
  const [guestForm, setGuestForm] = useState({
    name: '', email: '', phone: '', birth_date: '',
    party_type: 'single', gender: '', partner_name: '',
    partner_phone: '', partner_email: '', partner_birth_date: '',
    agreed_eligibility: false, agreed_terms: false,
  });

  // QR viewer modal — Promoter's ability to show/download QR for a confirmed guest
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrGuest, setQrGuest] = useState(null);      // { id, name, slot_number }
  const [qrDataUri, setQrDataUri] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);

  // Search filter inside the selected list
  const [search, setSearch] = useState('');

  // Auth guard
  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (!user.is_admin && !user.is_promoter) {
        toast.error('Promoter access required');
        navigate('/');
      }
    }
  }, [user, authLoading, navigate]);

  // Initial fetch
  useEffect(() => {
    const fetchAll = async () => {
      if (!token) return;
      try {
        const [eventsRes, listsRes] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/guest-lists`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setEvents(eventsRes.data);
        setGuestLists(listsRes.data);
      } catch {
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    if (token && (user?.is_admin || user?.is_promoter)) fetchAll();
  }, [token, user]);

  // Fetch guests for selected list
  useEffect(() => {
    const fetchGuests = async () => {
      if (!selectedListId || !token) return;
      try {
        const res = await axios.get(`${API}/guest-lists/${selectedListId}/guests`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setGuests(res.data);
      } catch {
        toast.error('Failed to load guests');
      }
    };
    fetchGuests();
  }, [selectedListId, token]);

  const refreshLists = async () => {
    const res = await axios.get(`${API}/guest-lists`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setGuestLists(res.data);
  };

  const handleCreateList = async (e) => {
    e.preventDefault();
    if (!codeLookup) {
      toast.error('Look up an event code first');
      return;
    }
    try {
      const payload = {
        event_id: codeLookup.id,
        name: listForm.name,
        wait_list_mode: !!listForm.wait_list_mode,
        ...(listForm.max_capacity && { max_capacity: parseInt(listForm.max_capacity) }),
      };
      await axios.post(`${API}/guest-lists`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Guest list created');
      setListDialogOpen(false);
      setListForm({ event_id: '', name: '', max_capacity: '', wait_list_mode: false });
      setCodeInput('');
      setCodeLookup(null);
      refreshLists();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    }
  };

  const handleLookupCode = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) {
      toast.error('Enter an event code');
      return;
    }
    setCodeLookupLoading(true);
    try {
      const res = await axios.get(`${API}/events/by-code/${code}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCodeLookup(res.data);
      toast.success(`Joined "${res.data.title}"`);
    } catch (err) {
      setCodeLookup(null);
      toast.error(err.response?.data?.detail || 'No event matches that code');
    } finally {
      setCodeLookupLoading(false);
    }
  };

  // Merge an updated list into local state without a full refetch
  const mergeListUpdate = (updated) => {
    setGuestLists((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
  };

  const refreshGuests = async () => {
    if (!selectedListId) return;
    const res = await axios.get(`${API}/guest-lists/${selectedListId}/guests`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setGuests(res.data);
    refreshLists();
  };

  const handleDeleteList = async (id) => {
    if (!window.confirm('Delete this list and all its guests?')) return;
    try {
      await axios.delete(`${API}/guest-lists/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('List deleted');
      if (selectedListId === id) setSelectedListId(null);
      refreshLists();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleAddGuest = async (e) => {
    e.preventDefault();
    if (!guestForm.agreed_eligibility || !guestForm.agreed_terms) {
      toast.error('Both consent boxes must be ticked');
      return;
    }
    if (!guestForm.birth_date) {
      toast.error('Enter guest\u2019s date of birth');
      return;
    }
    if (guestForm.party_type === 'single' && !['male', 'female'].includes(guestForm.gender)) {
      toast.error('Pick guest gender (male / female)');
      return;
    }
    if (guestForm.party_type === 'couple') {
      if (!['male_female', 'female_female'].includes(guestForm.gender)) {
        toast.error('Pick couple type (M+F or F+F)');
        return;
      }
      if (!guestForm.partner_name.trim()) {
        toast.error('Add the second person\u2019s name');
        return;
      }
      if (!guestForm.partner_phone.trim()) {
        toast.error('Add the second person\u2019s phone');
        return;
      }
      if (!guestForm.partner_birth_date) {
        toast.error('Add the second person\u2019s date of birth');
        return;
      }
    }
    try {
      const payload = {
        name: guestForm.name,
        email: guestForm.email || null,
        phone: guestForm.phone || null,
        birth_date: guestForm.birth_date,
        party_type: guestForm.party_type,
        gender: guestForm.gender,
        partner_name: guestForm.party_type === 'couple' ? guestForm.partner_name.trim() : null,
        partner_phone: guestForm.party_type === 'couple' ? guestForm.partner_phone.trim() : null,
        partner_email: guestForm.party_type === 'couple' && guestForm.partner_email.trim() ? guestForm.partner_email.trim() : null,
        partner_birth_date: guestForm.party_type === 'couple' ? guestForm.partner_birth_date : null,
        agreed_eligibility: true,
        agreed_terms: true,
      };
      await axios.post(`${API}/guest-lists/${selectedListId}/guests`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Guest added');
      setGuestDialogOpen(false);
      setGuestForm({
        name: '', email: '', phone: '', birth_date: '',
        party_type: 'single', gender: '', partner_name: '',
        partner_phone: '', partner_email: '', partner_birth_date: '',
        agreed_eligibility: false, agreed_terms: false,
      });
      const res = await axios.get(`${API}/guest-lists/${selectedListId}/guests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGuests(res.data);
      refreshLists();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    }
  };

  const handleApproveGuest = async (guestId) => {
    try {
      await axios.patch(`${API}/guests/${guestId}/status`, { status: 'confirmed' }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Guest approved — QR is now available');
      const res = await axios.get(`${API}/guest-lists/${selectedListId}/guests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGuests(res.data);
      refreshLists();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleShowQr = async (guest) => {
    setQrGuest({ id: guest.id, name: guest.name, slot_number: guest.slot_number });
    setQrDataUri(null);
    setQrDialogOpen(true);
    setQrLoading(true);
    try {
      const res = await axios.get(`${API}/rsvp/qr/${guest.id}`);
      setQrDataUri(res.data?.data_uri || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load QR');
      setQrDialogOpen(false);
    } finally {
      setQrLoading(false);
    }
  };

  const handleCheckIn = async (guestId) => {
    try {
      await axios.post(`${API}/guests/${guestId}/check-in`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await axios.get(`${API}/guest-lists/${selectedListId}/guests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGuests(res.data);
      refreshLists();
    } catch {
      toast.error('Failed to check in');
    }
  };

  const handleRemoveGuest = async (guestId) => {
    if (!window.confirm('Remove this guest?')) return;
    try {
      await axios.delete(`${API}/guests/${guestId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await axios.get(`${API}/guest-lists/${selectedListId}/guests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGuests(res.data);
      refreshLists();
    } catch {
      toast.error('Failed to remove');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-[#B5FF00] font-mono uppercase tracking-widest animate-pulse">
          Loading...
        </div>
      </div>
    );
  }

  const selectedList = guestLists.find((gl) => gl.id === selectedListId);
  const selectedEvent = selectedList ? events.find((ev) => ev.id === selectedList.event_id) : null;

  return (
    <div className="min-h-screen bg-[#050505] pt-20 sm:pt-24 pb-16" data-testid="promoter-dashboard">
      <div className="container mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="min-w-0">
            <span className="font-mono text-xs tracking-widest text-[#B5FF00] mb-2 block">
              PROMOTER PANEL
            </span>
            <h1 className="font-['Josefin_Sans'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white break-words">
              {selectedList ? selectedList.name.toUpperCase() : 'GUEST LISTS'}
            </h1>
          </div>
          {selectedListId ? (
            <button
              onClick={() => setGuestDialogOpen(true)}
              className="btn-primary flex items-center justify-center gap-2 w-full md:w-auto"
              data-testid="add-guest-btn"
            >
              <Plus size={18} />
              ADD GUEST
            </button>
          ) : (
            <button
              onClick={() => setListDialogOpen(true)}
              className="btn-primary flex items-center justify-center gap-2 w-full md:w-auto"
              data-testid="create-list-btn"
            >
              <Plus size={18} />
              NEW LIST
            </button>
          )}
        </div>

        {/* Back + event info when viewing a list */}
        {selectedListId && (
          <button
            onClick={() => setSelectedListId(null)}
            className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 font-mono text-xs tracking-widest"
            data-testid="back-to-lists-btn"
          >
            <ArrowLeft size={14} />
            BACK TO LISTS
          </button>
        )}

        {/* Lists grid */}
        {!selectedListId && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="lists-grid">
            {guestLists.map((gl) => {
              const event = events.find((ev) => ev.id === gl.event_id);
              return (
                <div
                  key={gl.id}
                  className="bg-[#0A0A0A] border border-zinc-800 hover:border-[#B5FF00]/50 transition-all p-6 cursor-pointer group"
                  onClick={() => setSelectedListId(gl.id)}
                  data-testid={`list-card-${gl.id}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-['Josefin_Sans'] text-xl text-white group-hover:text-[#B5FF00]">
                        {gl.name}
                      </h3>
                      {gl.code && (
                        <span className="inline-block mt-1 font-mono text-[10px] tracking-widest text-[#B5FF00] bg-[#B5FF00]/10 px-2 py-0.5">
                          {gl.code}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteList(gl.id);
                      }}
                      className="text-zinc-600 hover:text-red-500"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <p className="text-zinc-500 text-sm mb-4 font-mono">
                    {event?.title || 'Event'}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
                    <div className="flex items-center gap-2 text-zinc-400">
                      <Users size={14} />
                      <span className="font-mono text-sm">
                        {gl.guest_count}{gl.max_capacity ? ` / ${gl.max_capacity}` : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 font-mono text-xs">
                      {gl.waitlist_count > 0 && (
                        <span className="text-zinc-400">{gl.waitlist_count} WAIT</span>
                      )}
                      <span className="text-[#B5FF00]">{gl.checked_in_count} IN</span>
                    </div>
                  </div>
                </div>
              );
            })}
            {guestLists.length === 0 && (
              <div className="col-span-full text-center py-16 border border-zinc-800">
                <Users size={48} className="mx-auto text-zinc-700 mb-4" />
                <p className="text-zinc-500 font-mono">No guest lists yet. Create your first one.</p>
              </div>
            )}
          </div>
        )}

        {/* Guests table */}
        {selectedListId && (
          <div>
            {selectedEvent && (
              <div className="bg-[#0A0A0A] border border-zinc-800 p-4 mb-6 flex items-center gap-4">
                {selectedEvent.image_url && (
                  <img src={selectedEvent.image_url} alt="" className="w-16 h-16 object-cover" />
                )}
                <div>
                  <p className="font-['Josefin_Sans'] text-lg text-white">{selectedEvent.title}</p>
                  <p className="font-mono text-xs text-zinc-500 tracking-widest">
                    {selectedEvent.date} · {selectedEvent.venue}
                  </p>
                </div>
                <div className="ml-auto text-right">
                  <p className="font-['Josefin_Sans'] text-3xl text-[#B5FF00]">
                    {guests.filter((g) => g.checked_in).length}/{guests.length}
                  </p>
                  <p className="font-mono text-xs text-zinc-500 tracking-widest">CHECKED IN</p>
                </div>
              </div>
            )}

            {/* New demo blocks: share + live stats + toolbar */}
            {selectedList && (
              <>
                <ListShareCard
                  list={{ ...selectedList, event_title: selectedEvent?.title }}
                  token={token}
                  onUpdate={mergeListUpdate}
                />
                <StatsPanel listId={selectedList.id} token={token} capacity={selectedList.max_capacity} />
                <GuestListToolbar
                  list={selectedList}
                  token={token}
                  search={search}
                  setSearch={setSearch}
                  onChange={mergeListUpdate}
                  onAfterImport={refreshGuests}
                />
              </>
            )}

            <div className="overflow-x-auto">
              <table className="w-full" data-testid="guests-table">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left py-3 px-2 sm:py-4 sm:px-4 font-mono text-xs text-zinc-500 tracking-widest">#</th>
                    <th className="text-left py-3 px-2 sm:py-4 sm:px-4 font-mono text-xs text-zinc-500 tracking-widest">NAME</th>
                    <th className="text-left py-3 px-2 sm:py-4 sm:px-4 font-mono text-xs text-zinc-500 tracking-widest hidden md:table-cell">CONTACT</th>
                    <th className="text-left py-3 px-2 sm:py-4 sm:px-4 font-mono text-xs text-zinc-500 tracking-widest hidden lg:table-cell">SOURCE</th>
                    <th className="text-left py-3 px-2 sm:py-4 sm:px-4 font-mono text-xs text-zinc-500 tracking-widest">STATUS</th>
                    <th className="text-right py-3 px-2 sm:py-4 sm:px-4 font-mono text-xs text-zinc-500 tracking-widest">ACT</th>
                  </tr>
                </thead>
                <tbody>
                  {guests
                    .filter((g) => {
                      if (!search.trim()) return true;
                      const q = search.toLowerCase();
                      return (
                        (g.name || '').toLowerCase().includes(q) ||
                        (g.phone || '').toLowerCase().includes(q) ||
                        (g.email || '').toLowerCase().includes(q)
                      );
                    })
                    .map((g) => {
                    const isWait = g.status === 'waitlisted';
                    return (
                    <tr key={g.id} className="border-b border-zinc-800/50" data-testid={`guest-row-${g.id}`}>
                      <td className="py-3 px-2 sm:py-4 sm:px-4 font-mono text-xs text-zinc-500">{g.slot_number ?? '—'}</td>
                      <td className="py-3 px-2 sm:py-4 sm:px-4 text-white text-sm sm:text-base">
                        <div className="truncate max-w-[140px] sm:max-w-none">{g.name}</div>
                        {/* Mobile-only contact under the name */}
                        <div className="md:hidden text-zinc-500 text-xs font-mono truncate max-w-[140px]">
                          {g.phone || g.email || ''}
                        </div>
                      </td>
                      <td className="py-3 px-2 sm:py-4 sm:px-4 text-zinc-400 font-mono text-xs hidden md:table-cell">
                        {g.email && <div className="truncate max-w-[200px]">{g.email}</div>}
                        {g.phone && <div>{g.phone}</div>}
                      </td>
                      <td className="py-3 px-2 sm:py-4 sm:px-4 font-mono text-xs hidden lg:table-cell">
                        <span className={`px-2 py-1 ${
                          g.source === 'self_rsvp' ? 'bg-[#B5FF00]/10 text-[#B5FF00]' :
                          g.source === 'csv_import' ? 'bg-blue-500/10 text-blue-400' :
                          'bg-zinc-800 text-zinc-400'
                        }`}>
                          {(g.source || 'promoter').toUpperCase().replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-2 sm:py-4 sm:px-4">
                        {isWait ? (
                          <span className="font-mono text-[10px] sm:text-xs px-1.5 py-1 sm:px-2 bg-amber-500/10 text-amber-400 border border-amber-500/30 whitespace-nowrap">
                            WAITLIST
                          </span>
                        ) : g.checked_in ? (
                          <span className="font-mono text-[10px] sm:text-xs px-1.5 py-1 sm:px-2 bg-[#B5FF00]/10 text-[#B5FF00] border border-[#B5FF00]/30 whitespace-nowrap">
                            CHECKED IN
                          </span>
                        ) : (
                          <span className="font-mono text-[10px] sm:text-xs px-1.5 py-1 sm:px-2 bg-zinc-800 text-zinc-400 whitespace-nowrap">PENDING</span>
                        )}
                      </td>
                      <td className="py-3 px-2 sm:py-4 sm:px-4">
                        <div className="flex items-center justify-end gap-2">
                          {isWait && (
                            <PromoteButton
                              guestId={g.id}
                              token={token}
                              onPromoted={refreshGuests}
                            />
                          )}
                          {!isWait && (
                            <button
                              onClick={() => handleShowQr(g)}
                              className="p-2 text-zinc-400 hover:text-[#B5FF00]"
                              title="View / download QR"
                              data-testid={`qr-${g.id}`}
                            >
                              <QrCode size={18} />
                            </button>
                          )}
                          {!isWait && (
                            <button
                              onClick={() => handleCheckIn(g.id)}
                              className={`p-2 transition-colors ${
                                g.checked_in ? 'text-[#B5FF00]' : 'text-zinc-400 hover:text-[#B5FF00]'
                              }`}
                              title={g.checked_in ? 'Undo check-in' : 'Check in'}
                              data-testid={`checkin-${g.id}`}
                            >
                              {g.checked_in ? <X size={18} /> : <Check size={18} />}
                            </button>
                          )}
                          <button
                            onClick={() => handleRemoveGuest(g.id)}
                            className="p-2 text-zinc-400 hover:text-red-500"
                            data-testid={`remove-guest-${g.id}`}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              {guests.length === 0 && (
                <div className="text-center py-16 border border-zinc-800">
                  <p className="text-zinc-500 font-mono">No guests yet. Add your first one.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <CreateListDialog
        open={listDialogOpen}
        onOpenChange={(open) => {
          setListDialogOpen(open);
          if (!open) { setCodeInput(''); setCodeLookup(null); }
        }}
        codeInput={codeInput}
        setCodeInput={setCodeInput}
        codeLookup={codeLookup}
        codeLookupLoading={codeLookupLoading}
        onLookupCode={handleLookupCode}
        onResetLookup={() => { setCodeLookup(null); setCodeInput(''); }}
        listForm={listForm}
        setListForm={setListForm}
        onSubmit={handleCreateList}
      />
      <AddGuestDialog
        open={guestDialogOpen}
        onOpenChange={setGuestDialogOpen}
        guestForm={guestForm}
        setGuestForm={setGuestForm}
        onSubmit={handleAddGuest}
      />
      <GuestQrDialog
        open={qrDialogOpen}
        onOpenChange={setQrDialogOpen}
        guest={qrGuest}
        qrDataUri={qrDataUri}
        loading={qrLoading}
      />
    </div>
  );
};

export default PromoterDashboard;
